import { resolveMethodAssignment } from 'features/libra/inspectionPlanning/methodAssignments'
import { getPlanningStrategy } from 'features/libra/inspectionPlanning/planningStrategy'
import {
  InspectionArea,
  inspectionAreaSchema,
  PlanningStrategies,
  planningStrategiesSchema,
  sourceZonesSchema,
} from 'features/libra/inspectionPlanning/planningTypes'
import { isScopeCompatible } from 'features/libra/inspectionPlanning/scopeMeasurement'
import {
  MethodAssignment,
  scopeMeasurementSchema,
  TechniqueCatalogue,
  techniqueCatalogueSchema,
} from 'features/libra/inspectionPlanning/techniqueTypes'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { ZoneParams } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import type { LibraWorkflowGlobalContext } from 'features/libra/workflow/workflowStepsApi'
import isEqual from 'lodash/isEqual'
import { GeckoMeasurementJson, GeckoMeasurementUnit } from 'types'
import { z } from 'zod'

import {
  ExecutionColumn,
  ExecutionRow,
  executionRowsSchema,
  ExecutionSection,
  ExecutionValue,
} from './executionTypes'

const inspectionExecutionSchema = z.object({
  zones: sourceZonesSchema,
  areas: inspectionAreaSchema.array(),
  rawZones: z.array(z.record(z.string(), z.unknown())),
  rows: executionRowsSchema,
  strategies: planningStrategiesSchema,
  catalogue: techniqueCatalogueSchema,
})

export function readInspectionExecution(
  config: ExecutionSection,
  fields: FieldValues,
  globalContext: LibraWorkflowGlobalContext
) {
  const zones = unwrapFieldValue(fields[config.sourceZonesField]) ?? []
  return inspectionExecutionSchema.safeParse({
    zones,
    areas: unwrapFieldValue(fields[config.areasField]) ?? [],
    rawZones: zones,
    rows: unwrapFieldValue(fields[config.field]) ?? [],
    strategies: globalContext[config.strategiesContextKey],
    catalogue: globalContext[config.techniqueCatalogueContextKey],
  })
}

export function sourceMeasurementOptions(
  column: ExecutionColumn,
  zone: Record<string, unknown>
) {
  return column.sourceMeasurements.flatMap((source) => {
    const value = zone[source.field]
    const measurement = scopeMeasurementSchema.safeParse(value)
    const scope = measurement.success
      ? measurement.data
      : typeof value === 'number'
        ? { num: value, unit: source.unit }
        : undefined
    return scope && isScopeCompatible(scope, 'length')
      ? [{ label: source.label, value: scope }]
      : []
  })
}

export function createInspectionResultRows({
  zone,
  area,
  methods,
  columns,
  rawZone,
  strategies,
  catalogue,
  newId,
}: {
  zone: ZoneParams
  area: InspectionArea
  methods: MethodAssignment[]
  columns: ExecutionColumn[]
  rawZone: Record<string, unknown>
  strategies: PlanningStrategies
  catalogue: TechniqueCatalogue
  newId: () => string
}): ExecutionRow[] {
  if (area.sourceZoneId !== zone.id) return []
  const allowedMethods = area.methodAssignments
  return methods
    .filter((method) => allowedMethods.some((item) => item.id === method.id))
    .map((method) => ({
      id: newId(),
      sourceZoneId: zone.id,
      inspectionAreaId: area.id,
      methodAssignmentId: method.id,
      basis: {
        zoneTitle: zone.title,
        areaTitle: area.title,
        requiredCoveragePercent: getPlanningStrategy(zone, strategies)
          .plannedCoveragePercent,
        method: resolveMethodAssignment(method, catalogue),
      },
      values: Object.fromEntries(
        columns.flatMap<[string, ExecutionValue]>((column) => {
          if (column.type !== 'thickness')
            return column.defaultValue === undefined
              ? []
              : [[column.field, column.defaultValue]]
          const choices = sourceMeasurementOptions(column, rawZone)
          return choices.length === 1 ? [[column.field, choices[0].value]] : []
        })
      ),
    }))
}

export function appendInspectionResultRows(
  previous: ExecutionRow[],
  added: ExecutionRow[],
  allowRepeated: boolean
) {
  return added.reduce(
    (rows, row) =>
      !allowRepeated &&
      rows.some(
        (other) =>
          other.inspectionAreaId === row.inspectionAreaId &&
          other.methodAssignmentId === row.methodAssignmentId
      )
        ? rows
        : [...rows, row],
    previous
  )
}

export function inspectionResultLinkExists(
  row: ExecutionRow,
  zones: ZoneParams[],
  areas: InspectionArea[]
) {
  const area = areas.find(
    (item) =>
      item.id === row.inspectionAreaId && item.sourceZoneId === row.sourceZoneId
  )
  return (
    zones.some((zone) => zone.id === row.sourceZoneId) &&
    !!area &&
    area.methodAssignments.some(
      (method) => method.id === row.methodAssignmentId
    )
  )
}

export function inspectionResultBasisChanged(
  row: ExecutionRow,
  areas: InspectionArea[],
  catalogue: TechniqueCatalogue
) {
  const area = areas.find((item) => item.id === row.inspectionAreaId)
  const current =
    area &&
    area.methodAssignments.find(
      (method) => method.id === row.methodAssignmentId
    )
  return (
    !!current &&
    !isEqual(resolveMethodAssignment(current, catalogue), row.basis.method)
  )
}

export function isInspectionResultValueValid(
  column: ExecutionColumn,
  value: ExecutionValue,
  method: ExecutionRow['basis']['method']
) {
  if (value === undefined || value === '') return !column.required
  switch (column.type) {
    case 'text':
      return typeof value === 'string' && (!column.required || !!value.trim())
    case 'boolean':
      return typeof value === 'boolean'
    case 'number':
      return (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        (column.min === undefined || value >= column.min) &&
        (column.max === undefined || value <= column.max)
      )
    case 'scope':
    case 'thickness': {
      const scope = scopeMeasurementSchema.safeParse(value)
      const scopeType =
        column.type === 'thickness' ? 'length' : method.scopeType
      return (
        !!scopeType && scope.success && isScopeCompatible(scope.data, scopeType)
      )
    }
  }
}

export function isExecutionSectionComplete(
  config: ExecutionSection,
  fields: FieldValues,
  globalContext: LibraWorkflowGlobalContext
) {
  const execution = readInspectionExecution(config, fields, globalContext)
  if (!execution.success) return false
  const { zones, areas, rows } = execution.data
  return rows.every(
    (row) =>
      inspectionResultLinkExists(row, zones, areas) &&
      config.columns.every((column) =>
        isInspectionResultValueValid(
          column,
          row.values[column.field],
          row.basis.method
        )
      )
  )
}

export function thicknessUnit(imperial: boolean) {
  return imperial
    ? GeckoMeasurementUnit.Inches
    : GeckoMeasurementUnit.Millimeters
}

export function asMeasurement(
  value: ExecutionValue
): GeckoMeasurementJson | undefined {
  const parsed = scopeMeasurementSchema.safeParse(value)
  return parsed.success ? parsed.data : undefined
}
